import { useCallback, useEffect, useState } from 'react';
import { api } from '../services/api';
// Styles live with the Word Bank screens (wb-*); imported here since this view now sits on the Content tab.
import './WordBankPage.css';

interface AuditField {
  field: string;
  verdict: 'pass' | 'fail';
  reason: string | null;
  rule_source: string | null;
  override: boolean;
  override_at: string | null;
}

interface AuditWord {
  word_id: string;
  word: string;
  bucket: string;
  related_record_id: string | null;
  audited_at: string | null;
  model: string | null;
  rules_sha: Record<string, string> | null;
  social_status: string | null;
  flag_note: string | null;
  fields: AuditField[];
  total_fields: number;
  failed_fields: number;
  blocking_fields: number;
  clean: boolean;
}

interface AuditResponse {
  words: AuditWord[];
  totals: { audited_words: number; clean: number; with_failures: number };
}

/** One field as the detail endpoint returns it: the grade AND the stored text. */
interface DetailField {
  field: string;
  verdict: 'pass' | 'fail';
  reason: string | null;
  rule_source: string | null;
  override: boolean;
  override_at: string | null;
  override_by: string | null;
  audited_at: string | null;
  model: string | null;
  regen_count: number;
  text: string | null;
  is_fake: boolean;
  editable: boolean;
}

interface AuditDetail {
  word_id: string;
  word: string;
  bucket: string;
  related_record_id: string | null;
  audit_status: string | null;
  social_status: string | null;
  regen_passes: number;
  write_error: string | null;
  real_definition: string | null;
  definition_category: string | null;
  fields: DetailField[];
}

interface FieldActionResult {
  field: string;
  verdict: 'pass' | 'fail';
  reason: string | null;
  audit_status: string | null;
  regen_count: number;
  error?: string;
}

/** Short label for a field chip. */
function chipLabel(field: string): string {
  if (field === 'qa_linter') return 'lint';
  return field
    .replace('definition_enhanced', 'def+')
    .replace('definition_w', 'fake')
    .replace('fun_fact_', 'fact')
    .replace('example_sentence_', 'ex')
    .replace('how_to_use_', 'use')
    .replace('pronunciation_tip', 'tip');
}

function when(ts: string | null): string {
  return ts ? new Date(ts).toLocaleString() : '—';
}

export default function ContentAuditView() {
  const [data, setData] = useState<AuditResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [failsOnly, setFailsOnly] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState<Set<string>>(new Set());
  const [migrated, setMigrated] = useState<Record<string, string>>({});

  // Per-word detail, fetched on expand.
  const [detail, setDetail] = useState<Record<string, AuditDetail>>({});
  const [detailErr, setDetailErr] = useState<Record<string, string>>({});
  // Which field is mid-action, and the inline editor's buffer.
  const [fieldBusy, setFieldBusy] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState<Record<string, string>>({});
  const [lastResult, setLastResult] = useState<Record<string, FieldActionResult>>({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = (await api.get(`/api/wordbank/audit${failsOnly ? '?fails=1' : ''}`)) as AuditResponse;
      setData(res);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [failsOnly]);

  useEffect(() => { load(); }, [load]);

  const mark = (id: string, on: boolean) =>
    setBusy((b) => { const n = new Set(b); if (on) n.add(id); else n.delete(id); return n; });

  const markField = (key: string, on: boolean) =>
    setFieldBusy((b) => { const n = new Set(b); if (on) n.add(key); else n.delete(key); return n; });

  const loadDetail = useCallback(async (wordId: string) => {
    try {
      const d = (await api.get(`/api/wordbank/${wordId}/audit-detail`)) as AuditDetail;
      setDetail((m) => ({ ...m, [wordId]: d }));
      setDetailErr((m) => { const n = { ...m }; delete n[wordId]; return n; });
    } catch (err) {
      setDetailErr((m) => ({ ...m, [wordId]: (err as Error).message }));
    }
  }, []);

  const migrate = useCallback(async (w: AuditWord) => {
    mark(w.word_id, true);
    try {
      const res = (await api.post(`/api/words/${w.word_id}/migrate`, {})) as {
        clean?: boolean; diff?: unknown[]; fields_compared?: number;
      };
      setMigrated((m) => ({
        ...m,
        [w.word_id]: res.clean
          ? `migrated, ${res.fields_compared} fields, zero loss`
          : `NOT migrated — ${res.diff?.length ?? 0} field diff`,
      }));
    } catch (err) {
      setMigrated((m) => ({ ...m, [w.word_id]: `failed: ${(err as Error).message}` }));
    } finally {
      mark(w.word_id, false);
    }
  }, []);

  const acceptAnyway = useCallback(async (w: AuditWord) => {
    mark(w.word_id, true);
    try {
      await api.post(`/api/wordbank/${w.word_id}/audit-override`, {});
      await load();
      if (expanded.has(w.word_id)) await loadDetail(w.word_id);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      mark(w.word_id, false);
    }
  }, [load, loadDetail, expanded]);

  /** Regenerate / Edit / Accept, one field at a time. */
  const fieldAction = useCallback(async (
    wordId: string,
    field: string,
    action: 'regenerate' | 'edit' | 'accept',
  ) => {
    const key = `${wordId}:${field}`;
    markField(key, true);
    setLastResult((m) => { const n = { ...m }; delete n[key]; return n; });
    try {
      let res: FieldActionResult;
      if (action === 'accept') {
        await api.post(`/api/wordbank/${wordId}/audit-override`, { field });
        res = { field, verdict: 'fail', reason: 'accepted by Odin', audit_status: null, regen_count: 0 };
      } else if (action === 'edit') {
        res = (await api.post(`/api/wordbank/${wordId}/audit-field/edit`, {
          field, text: editing[key] ?? '',
        })) as FieldActionResult;
      } else {
        res = (await api.post(`/api/wordbank/${wordId}/audit-field/regenerate`, { field })) as FieldActionResult;
      }
      setLastResult((m) => ({ ...m, [key]: res }));
      if (action === 'edit') setEditing((m) => { const n = { ...m }; delete n[key]; return n; });
      await loadDetail(wordId);
      await load();
    } catch (err) {
      setLastResult((m) => ({
        ...m,
        [key]: { field, verdict: 'fail', reason: null, audit_status: null, regen_count: 0, error: (err as Error).message },
      }));
    } finally {
      markField(key, false);
    }
  }, [editing, load, loadDetail]);

  const toggle = (id: string) => setExpanded((e) => {
    const n = new Set(e);
    if (n.has(id)) { n.delete(id); } else { n.add(id); void loadDetail(id); }
    return n;
  });

  return (
    <div className="wb-audit">
      <div className="wb-filters">
        <label className="wb-check">
          <input type="checkbox" checked={failsOnly} onChange={(e) => setFailsOnly(e.target.checked)} />
          Failures only
        </label>
        {data && (
          <span className="wb-totals">
            <span><b>{data.totals.audited_words}</b> audited</span>
            <span><b>{data.totals.clean}</b> clean</span>
            <span><b>{data.totals.with_failures}</b> with failures</span>
          </span>
        )}
        <button className="wb-refresh" onClick={load} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {error && <div className="wb-error">{error}</div>}
      {!loading && data && data.words.length === 0 && (
        <div className="wb-empty">Nothing audited yet.</div>
      )}

      {data && data.words.length > 0 && (
        <table className="wb-table">
          <thead>
            <tr>
              <th className="wb-col-word">Word</th>
              <th>Fields</th>
              <th className="wb-col-actions">Action</th>
            </tr>
          </thead>
          <tbody>
            {data.words.map((w) => {
              const isOpen = expanded.has(w.word_id);
              const isBusy = busy.has(w.word_id);
              const note = migrated[w.word_id];
              const d = detail[w.word_id];
              const failing = d ? d.fields.filter((f) => f.verdict === 'fail' && !f.override) : [];
              return [
                <tr key={w.word_id} className={`wb-row ${w.clean ? '' : 'wb-disagree'}`}>
                  <td className="wb-col-word">
                    <div className="wb-word-cell">
                      <button className="wb-expand" onClick={() => toggle(w.word_id)} aria-expanded={isOpen}>
                        {isOpen ? '▾' : '▸'}
                      </button>
                      <div className="wb-word-main">
                        <div className="wb-word-line">
                          <span className="wb-word">{w.word}</span>
                          {w.clean
                            ? <span className="wb-chip wb-chip-pass">all pass</span>
                            : <span className="wb-chip wb-chip-fail">{w.blocking_fields} blocking</span>}
                        </div>
                        {note && <div className="wb-def">{note}</div>}
                        {w.social_status === 'flagged' && (
                          <div className="wb-note" title="Flagged in Related Review">
                            flagged by Odin{w.flag_note ? `: “${w.flag_note}”` : ' (no note given)'}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="wb-chips">
                      {w.fields.map((f) => (
                        <span
                          key={f.field}
                          className={`wb-chip ${f.verdict === 'pass' ? 'wb-chip-pass' : f.override ? 'wb-chip-override' : 'wb-chip-fail'}`}
                          title={`${f.field} (${f.rule_source ?? '—'}) — ${f.verdict.toUpperCase()}${f.override ? ', OVERRIDDEN' : ''}\n${f.reason ?? ''}`}
                        >
                          {chipLabel(f.field)}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="wb-col-actions">
                    <button
                      className="wb-accept"
                      disabled={isBusy || !w.clean}
                      onClick={() => migrate(w)}
                      title={w.clean ? 'Migrate this word into D1' : 'Blocked: at least one field fails'}
                    >
                      Migrate
                    </button>
                    <button
                      className="wb-move"
                      disabled={isBusy || w.clean}
                      onClick={() => acceptAnyway(w)}
                      title="Override every failing field on this word"
                    >
                      Accept all
                    </button>
                  </td>
                </tr>,
                isOpen && (
                  <tr key={`${w.word_id}-x`} className="wb-detail-row">
                    <td colSpan={3}>
                      <div className="wb-detail">
                        {detailErr[w.word_id] && <div className="wb-error">{detailErr[w.word_id]}</div>}
                        {!d && !detailErr[w.word_id] && <div className="wb-empty">Loading detail…</div>}

                        {d && (
                          <div className="wb-detail-block wb-audit-fields">
                            <h4>
                              Failing fields{failing.length ? ` (${failing.length})` : ''}
                              <span className="wb-meta">
                                {'  '}word status: {d.audit_status ?? '—'} · {d.regen_passes} word-level pass(es)
                              </span>
                            </h4>

                            {failing.length === 0 && (
                              <p className="wb-empty">
                                Nothing failing. Every field passes or has been accepted.
                              </p>
                            )}

                            {failing.map((f) => {
                              const key = `${w.word_id}:${f.field}`;
                              const fBusy = fieldBusy.has(key);
                              const isEditing = key in editing;
                              const result = lastResult[key];
                              return (
                                <div key={f.field} className="wb-audit-field">
                                  <div className="wb-audit-field-head">
                                    <span className="wb-reason-axis">{f.field}</span>
                                    <span className="wb-chip wb-chip-fail">fail</span>
                                    <span className="wb-meta">
                                      {f.rule_source ?? '—'} · retried {f.regen_count}× · graded {when(f.audited_at)}
                                    </span>
                                  </div>

                                  <p className="wb-reason-text"><b>Why it failed:</b> {f.reason ?? '—'}</p>

                                  {f.is_fake && d.real_definition && (
                                    <p className="wb-note">
                                      <b>Real definition{d.definition_category ? ` (${d.definition_category})` : ''}:</b>{' '}
                                      {d.real_definition}
                                    </p>
                                  )}

                                  {isEditing ? (
                                    <div className="wb-audit-edit">
                                      <textarea
                                        className="wb-textarea"
                                        rows={3}
                                        value={editing[key]}
                                        onChange={(e) => setEditing((m) => ({ ...m, [key]: e.target.value }))}
                                      />
                                      <div className="wb-audit-actions">
                                        <button
                                          className="wb-accept"
                                          disabled={fBusy || !editing[key]?.trim()}
                                          onClick={() => fieldAction(w.word_id, f.field, 'edit')}
                                        >
                                          {fBusy ? 'Saving…' : 'Save and re-grade'}
                                        </button>
                                        <button
                                          className="wb-move"
                                          disabled={fBusy}
                                          onClick={() => setEditing((m) => { const n = { ...m }; delete n[key]; return n; })}
                                        >
                                          Cancel
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <>
                                      <pre className="wb-audit-text">{f.text || '(empty)'}</pre>
                                      <div className="wb-audit-actions">
                                        <button
                                          className="wb-accept"
                                          disabled={fBusy}
                                          onClick={() => fieldAction(w.word_id, f.field, 'regenerate')}
                                          title="Rewrite this field only, with its siblings as context. Fakes stay blind."
                                        >
                                          {fBusy ? 'Working…' : 'Regenerate'}
                                        </button>
                                        <button
                                          className="wb-move"
                                          disabled={fBusy}
                                          onClick={() => setEditing((m) => ({ ...m, [key]: f.text ?? '' }))}
                                        >
                                          Edit
                                        </button>
                                        <button
                                          className="wb-move"
                                          disabled={fBusy}
                                          onClick={() => fieldAction(w.word_id, f.field, 'accept')}
                                          title="Keep the text and override the failure. The verdict and reason are kept."
                                        >
                                          Accept
                                        </button>
                                      </div>
                                    </>
                                  )}

                                  {result && (
                                    <p className={result.error ? 'wb-error' : 'wb-note'}>
                                      {result.error
                                        ? result.error
                                        : `now ${result.verdict}${result.reason ? ` — ${result.reason}` : ''}`}
                                    </p>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}

                        <div className="wb-detail-block">
                          <h4>All grades</h4>
                          <ul className="wb-reasons">
                            {(d ? d.fields : w.fields).map((f) => (
                              <li key={f.field}>
                                <span className="wb-reason-axis">{f.field}</span>
                                <span className={`wb-chip ${f.verdict === 'pass' ? 'wb-chip-pass' : f.override ? 'wb-chip-override' : 'wb-chip-fail'}`}>
                                  {f.override ? 'override' : f.verdict}
                                </span>
                                <span className="wb-reason-text">{f.reason ?? '—'}</span>
                              </li>
                            ))}
                          </ul>
                        </div>

                        <div className="wb-detail-block">
                          <h4>Audit provenance</h4>
                          <p><b>Graded by:</b> {w.model ?? '—'}</p>
                          <p><b>When:</b> {when(w.audited_at)}</p>
                          <p><b>Airtable record:</b> {w.related_record_id ?? '—'}</p>
                          {d?.write_error && <p className="wb-error"><b>Write error:</b> {d.write_error}</p>}
                          <p className="wb-meta">
                            Rule versions graded against —{' '}
                            {w.rules_sha
                              ? Object.entries(w.rules_sha).map(([k, v]) => `${k}:${v}`).join(' · ')
                              : '—'}
                          </p>
                        </div>
                      </div>
                    </td>
                  </tr>
                ),
              ];
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
